import Vue from 'vue';
import NimSRegistrationRequestModel from '../../generated/types/model/resource/NimSRegistrationRequestModel';
import RequestSummaryResourceViewModel from './RequestSummaryResourceViewModel';
import RequestSummarySectionResourceViewModel from './RequestSummarySectionResourceViewModel';
import RequestSummaryItemResourceViewModel from './RequestSummaryItemResourceViewModel';
import GuidLookupResourceViewModel from './GuidLookupResourceViewModel';

export default class NimSRegistrationRequestViewModel extends NimSRegistrationRequestModel {
  nimsLanguages: Array<GuidLookupResourceViewModel> = [];

  translatedItemsList: Array<GuidLookupResourceViewModel> = [];

  get isNatureOfferAdvice():boolean {
    return (this.natureOffer ?? '').toUpperCase() === Vue.$config.values['natureoffer-advice'].toUpperCase();
  }

  get isNatureOfferCertification():boolean {
    return (this.natureOffer ?? '').toUpperCase() === Vue.$config.values['natureoffer-certification'].toUpperCase();
  }

  get nimsTopicKeyFromQuery() { return this.getFromAttributeOrQuery<string>('nimstopickey'); }

  async loadLanguages() {
    this.nimsLanguages = (await Vue.$service.v1.api.widgetNimS.lookupCorrespondenceLanguages()).items.map((l) => { return { id: l.id, caption: Vue.$t(`core.page.language.${l.caption?.toLowerCase()}`) } as GuidLookupResourceViewModel; });
    this.translatedItemsList = (await Vue.$service.v1.api.widgetNimSGardenAdvices.getNimSGardenAdvices()).items
      .concat((await Vue.$service.v1.api.widgetNimSGardenCertifications.getNimSGardenCertifications()).items)
      .concat((await Vue.$service.v1.api.widgetNimS.lookupCorrespondenceLanguages()).items)
      .concat((await Vue.$service.v1.api.widgetNimSTopics.getNimSTopics()).items)
      .concat((await Vue.$service.v1.api.widgetNimSCertificats.getNimSCertificates()).items)
      .concat((await Vue.$service.v1.api.widgetNimSGardenTypes.getNimSGardenTypes()).items);
  }

  getFromAttributeOrQuery<T>(key: string): T | undefined {
    key = key.toLowerCase();
    const queries = Vue.$routerUtils.getQueryParams();
    const queriesLowerCase = {} as any;
    Object.keys(queries).forEach((queryKey) => {
      queriesLowerCase[queryKey.toLowerCase()] = queries[queryKey];
    });
    const configKey = `widget-${key}`;
    const value = (Vue.$config.values as any)[configKey] ?? Vue.$router.currentRoute.params[key] ?? queriesLowerCase[key];
    return value;
  }

  protected transformToDTO() {
    this.dto.summary = this.summary;
  }

  // For instructions on how to use ViewModels visit:
  // https://glittrsoftware.visualstudio.com/glittr.core/_wiki/wikis/glittr.core.wiki/155/ViewModels-Models-DTOs
  get summary():RequestSummaryResourceViewModel {
    const itemsData:RequestSummaryItemResourceViewModel[] = [];
    itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
      displayValue: Vue.$t(this.isNatureOfferAdvice ? 'pronatura.widget.nimswizard.summary.gardenAdvice' : 'pronatura.widget.nimswizard.summary.gardenCertificate'),
      label: Vue.$t('pronatura.widget.nimswizard.summary.nims.topic'),
      showLabelOnMobile: true,
    }));
    if (this.isNatureOfferAdvice) {
      Vue.$log.info(this.gardenAdvice);
      for (let index = 0; index < (this.gardenAdvice?.gardenAdviceList?.length ?? 0); index += 1) {
        const element = this.gardenAdvice?.gardenAdviceList![index];
        itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.translatedItemsList?.find((x) => x.id === element)?.caption,
          label: index === 0 ? Vue.$t('pronatura.widget.nimswizard.summary.gardenAdvice.gardenAdviceList') : '',
          showLabelOnMobile: true,
        }));
      }
      if ((this.gardenAdvice?.otherUpgrade ?? '').length > 0) {
        itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.gardenAdvice?.otherUpgrade,
          label: this.gardenAdvice?.gardenAdviceList?.length === 0 ? Vue.$t('pronatura.widget.nimswizard.summary.gardenAdvice.gardenAdviceList') : '',
          showLabelOnMobile: true,
        }));
      }
    }
    if (this.isNatureOfferCertification) {
      for (let index = 0; index < (this.gardenCertification?.minimumRequirementsList?.length ?? 0); index += 1) {
        const element = this.gardenCertification?.minimumRequirementsList![index];
        itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.translatedItemsList?.find((x) => x.id === element)?.caption,
          label: index === 0 ? Vue.$t('pronatura.widget.nimswizard.summary.gardenCertification.minimumRequirementsList') : '',
          showLabelOnMobile: true,
        }));
      }
      itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
        displayValue: this.gardenCertification.wasAlreadyCertificated ? Vue.$t('core.app.yes') : Vue.$t('core.app.no'),
        label: Vue.$t('pronatura.widget.nimswizard.summary.gardenCertification.wasAlreadyCertificated'),
        showLabelOnMobile: true,
      }));
      if (this.gardenCertification.wasAlreadyCertificated) {
        itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.translatedItemsList?.find((x) => x.id === this.gardenCertification.certificate)?.caption,
          label: Vue.$t('pronatura.widget.nimswizard.summary.gardenCertification.certificate'),
          showLabelOnMobile: true,
        }));
      }
    }
    if (this.gardenSize) {
      itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
        displayValue: this.gardenSize.toString(),
        label: Vue.$t('pronatura.widget.nimswizard.summary.gardenSize'),
        showLabelOnMobile: true,
      }));
    }
    if ((this.communicationTime ?? '').length > 0) {
      itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
        displayValue: this.communicationTime,
        label: Vue.$t('pronatura.widget.nimswizard.summary.communicationTime'),
        showLabelOnMobile: true,
      }));
    }
    if ((this.remarks ?? '').length > 0) {
      itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
        displayValue: this.remarks,
        label: Vue.$t('pronatura.widget.nimswizard.summary.remarks'),
        showLabelOnMobile: true,
      }));
    }
    if (this.gardenType) {
      itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
        displayValue: this.translatedItemsList?.find((x) => x.id === this.gardenType)?.caption,
        label: Vue.$t('pronatura.widget.nimswizard.gardenInformation.gardenTypeLabel'),
        showLabelOnMobile: true,
      }));
    }
    itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
      displayValue: this.isGardenOwner ? Vue.$t('core.app.yes') : Vue.$t('core.app.no'),
      label: Vue.$t('pronatura.widget.nimswizard.summary.isGardenOwner'),
      showLabelOnMobile: true,
    }));

    if (this.isGardenOwner === false) {
      itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
        displayValue: this.hasAcceptedRegulations ? Vue.$t('core.app.yes') : Vue.$t('core.app.no'),
        label: Vue.$t('pronatura.widget.nimswizard.summary.hasAcceptedRegulations'),
        showLabelOnMobile: true,
      }));
    }
    // itemsData.push(new RequestSummaryItemResourceViewModel().fromModel({
    //   displayValue: this.hasAcceptedGdpr ? Vue.$t('core.app.yes') : Vue.$t('core.app.no'),
    //   label: Vue.$tEnum('pronatura.widget.nimswizard.summary.accept_terms', 'no_html'),
    //   showLabelOnMobile: true,
    // }));
    const sections:RequestSummarySectionResourceViewModel[] = [
      new RequestSummarySectionResourceViewModel().fromModel(
        { items: itemsData.filter((i) => Vue._.isSet(i.displayValue)) },
      )];
    sections.push(new RequestSummarySectionResourceViewModel().fromModel({
      title: Vue.$t('pronatura.widget.nimswizard.summary.contact'),
      items: [
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.salutation === 3 ? '' : Vue.$t(`core.address.salutationEnum${this.contactData?.salutation}`),
          label: Vue.$t('core.address.salutation'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.firstName,
          label: Vue.$t('core.address.firstName'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.lastName,
          label: Vue.$t('core.address.lastName'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.street,
          label: Vue.$t('core.address.street'),
          showLabelOnMobile: true,
        }), new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.houseNumber,
          label: Vue.$t('core.address.houseNumber'),
          showLabelOnMobile: true,
        }), new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.zipCode,
          label: Vue.$t('core.address.postalCode'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.city,
          label: Vue.$t('core.address.city'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.contactData?.countryISO,
          label: Vue.$t('core.address.country'),
          showLabelOnMobile: true,
        })].filter((i) => Vue._.isSet(i.displayValue)),
    }));

    if (this.isGardenAddressNotContactDataAddress) {
      sections.push(new RequestSummarySectionResourceViewModel().fromModel({
        title: Vue.$t('pronatura.widget.nimswizard.summary.gardenAddress'),
        items: [
          new RequestSummaryItemResourceViewModel().fromModel({
            displayValue: this.gardenAddress.street,
            label: Vue.$t('core.address.street'),
            showLabelOnMobile: true,
          }), new RequestSummaryItemResourceViewModel().fromModel({
            displayValue: this.gardenAddress.houseNumber,
            label: Vue.$t('core.address.houseNumber'),
            showLabelOnMobile: true,
          }), new RequestSummaryItemResourceViewModel().fromModel({
            displayValue: this.gardenAddress.zipCode,
            label: Vue.$t('core.address.postalCode'),
            showLabelOnMobile: true,
          }),
          new RequestSummaryItemResourceViewModel().fromModel({
            displayValue: this.gardenAddress.city,
            label: Vue.$t('core.address.city'),
            showLabelOnMobile: true,
          }),
        ],
      }));
    }
    sections.push(new RequestSummarySectionResourceViewModel().fromModel({
      title: Vue.$t('pronatura.widget.nimswizard.summary.communication'),
      items: [
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.communicationData?.phoneNumber,
          label: Vue.$t('core.address.phoneNumber'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.communicationData?.email,
          label: Vue.$t('core.address.email'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.hasAcceptedMarketing ? Vue.$t('core.app.yes') : Vue.$t('core.app.no'),
          label: Vue.$t('pronatura.widget.nimswizard.summary.hasAcceptedMarketing'),
          showLabelOnMobile: true,
        }),
        new RequestSummaryItemResourceViewModel().fromModel({
          displayValue: this.nimsLanguages?.find((x) => x.id === this.nimSLanguageId)?.caption,
          label: Vue.$t('pronatura.widget.membershipwizard.language_title'),
          showLabelOnMobile: true,
        }),
      ].filter((i) => Vue._.isSet(i.displayValue)),
    }));
    return new RequestSummaryResourceViewModel().fromModel({
      sections,
      languageIso2: this.nimSLanguageId,
    });
  }
}
